// @ts-nocheck
import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Search, Users, CheckCircle2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TeamSearchMatch {
  team_id: string;
  canonical_name: string;
  display_name: string;
  stable_slug: string;
  age_group: string | null;
  gender: string | null;
  home_state: string | null;
  primary_coach_name: string | null;
  club_name: string | null;
  match_score: number;
  match_reasons: string[];
  classification: 'auto_match' | 'suggest' | 'inform' | 'none';
  user_is_member: boolean;
  user_can_register: boolean;
  ownership_status: string;
}

interface TeamSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuery?: string;
  ageGroupFilter?: string;
  organizationId: string;
  embedded?: boolean;
  onTeamSelected: (team: TeamSearchMatch) => void;
  onCreateNew: (query: string) => void;
  onJoinRequested: (teamId: string, message: string) => void;
}

interface SearchResponse {
  ok: boolean;
  data?: {
    matches: TeamSearchMatch[];
    top_classification: string;
    search_event_id: string;
  };
  error?: { code: string; message: string };
}

function getScoreVariant(score: number): {
  className: string;
  label: string;
} {
  if (score >= 90) {
    return {
      className: 'bg-green-100 text-green-800 border-green-300',
      label: `${score}% match`,
    };
  }
  if (score >= 70) {
    return {
      className: 'bg-amber-100 text-amber-800 border-amber-300',
      label: `${score}% match`,
    };
  }
  return {
    className: 'bg-gray-100 text-gray-700 border-gray-300',
    label: `${score}% match`,
  };
}

function TeamMatchCard({
  match,
  onTeamSelected,
  onJoinRequested,
}: {
  match: TeamSearchMatch;
  onTeamSelected: (team: TeamSearchMatch) => void;
  onJoinRequested: (teamId: string, message: string) => void;
}) {
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const score = getScoreVariant(match.match_score);
  const isAutoMatch = match.classification === 'auto_match';

  const subtitleParts = [match.age_group, match.home_state].filter(Boolean);

  return (
    <Card
      className={cn(
        'p-4 transition-all hover:shadow-md',
        isAutoMatch && 'border-l-4 border-l-green-500'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-base truncate">
              {match.display_name || match.canonical_name}
            </h3>
            {match.user_is_member && (
              <Badge variant="success" className="shrink-0">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                You're a member
              </Badge>
            )}
          </div>
          {subtitleParts.length > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {subtitleParts.join(' · ')}
            </p>
          )}
          {match.club_name && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {match.club_name}
            </p>
          )}
          {match.primary_coach_name && (
            <Badge variant="outline" className="mt-2">
              Coach: {match.primary_coach_name}
            </Badge>
          )}
          {match.match_reasons && match.match_reasons.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {match.match_reasons.map((reason, i) => (
                <span
                  key={i}
                  className="text-xs text-muted-foreground italic"
                >
                  {reason}
                  {i < match.match_reasons.length - 1 ? ' ·' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
        <Badge
          variant="outline"
          className={cn('shrink-0 font-semibold', score.className)}
        >
          {score.label}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {match.user_is_member ? (
          <Button
            size="sm"
            onClick={() => onTeamSelected(match)}
            className="flex-1 sm:flex-none"
          >
            <Users className="w-4 h-4 mr-1" />
            Open team
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              onClick={() => onTeamSelected(match)}
              className="flex-1 sm:flex-none"
            >
              This is my team
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowJoinForm((v) => !v)}
              className="flex-1 sm:flex-none"
            >
              Request to manage
            </Button>
          </>
        )}
      </div>

      {showJoinForm && !match.user_is_member && (
        <div className="mt-3 space-y-2">
          <Textarea
            placeholder="Optional message to the team owner..."
            value={joinMessage}
            onChange={(e) => setJoinMessage(e.target.value)}
            rows={3}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                onJoinRequested(match.team_id, joinMessage);
                setShowJoinForm(false);
                setJoinMessage('');
              }}
            >
              Send request
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowJoinForm(false);
                setJoinMessage('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export function TeamSearchDialog({
  open,
  onOpenChange,
  initialQuery = '',
  ageGroupFilter,
  organizationId,
  embedded = false,
  onTeamSelected,
  onCreateNew,
  onJoinRequested,
}: TeamSearchDialogProps) {
  const [query, setQuery] = useState(initialQuery);
  const [immediateQuery, setImmediateQuery] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query, 350);
  const lastResultsRef = useRef<TeamSearchMatch[]>([]);

  // Effective query: immediate (Enter pressed) takes precedence over debounced
  const effectiveQuery = immediateQuery !== null ? immediateQuery : debouncedQuery;

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setImmediateQuery(null);
    }
  }, [open, initialQuery]);

  const trimmedQuery = effectiveQuery.trim();
  const hasQuery = trimmedQuery.length > 0;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['rle-search-teams', trimmedQuery, ageGroupFilter, organizationId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<SearchResponse>(
        'rle-search-teams',
        {
          body: {
            query: trimmedQuery,
            age_group: ageGroupFilter,
            organization_id: organizationId,
          },
        }
      );

      if (error) throw new Error(error.message);
      if (!data?.ok) {
        throw new Error(data?.error?.message || 'Search failed');
      }
      return data.data;
    },
    enabled: hasQuery,
  });

  useEffect(() => {
    if (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to search teams'
      );
    }
  }, [error]);

  const matches = data?.matches ?? [];
  if (matches.length > 0) {
    lastResultsRef.current = matches;
  }
  const displayedMatches =
    isFetching && matches.length === 0 && lastResultsRef.current.length > 0
      ? lastResultsRef.current
      : matches;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setImmediateQuery(query);
    }
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setImmediateQuery(null);
  };

  const content = (
    <div className="flex flex-col gap-4 min-h-0">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          autoFocus
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search by team name..."
          className="pl-9"
        />
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 min-h-0 max-h-[50vh]">
        {!hasQuery && (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Start typing your team name to search
          </div>
        )}

        {hasQuery && isLoading && (
          <>
            {[0, 1, 2].map((i) => (
              <Card key={i} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-6 w-32 mt-2" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
                <div className="flex gap-2 mt-4">
                  <Skeleton className="h-9 w-32" />
                  <Skeleton className="h-9 w-32" />
                </div>
              </Card>
            ))}
          </>
        )}

        {hasQuery && !isLoading && displayedMatches.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No teams found
          </div>
        )}

        {displayedMatches.map((match) => (
          <TeamMatchCard
            key={match.team_id}
            match={match}
            onTeamSelected={onTeamSelected}
            onJoinRequested={onJoinRequested}
          />
        ))}
      </div>

      {hasQuery && (
        <>
          <Separator />
          <div className="sticky bottom-0 bg-background pt-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onCreateNew(trimmedQuery)}
            >
              <Plus className="w-4 h-4 mr-1" />
              None of these match — create new team with "{trimmedQuery}"
            </Button>
          </div>
        </>
      )}
    </div>
  );

  if (embedded) {
    return (
      <Card className="p-4">
        <h2 className="text-lg font-semibold mb-4">Find your team</h2>
        {content}
      </Card>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Find your team</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}

export default TeamSearchDialog;